import React, { useEffect, useState } from 'react';

import { RoleInterFace } from '../../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { fetchAllPermissions, fetchAllRoles } from '../../../redux/slice/RoleManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-button/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import { stringValidation } from '../../../shared-functions/Validation';
import roleApi from '../../../utils/api/roles';

interface RoleDetailInterface {
  name: string;
  responsibility: string | null;
  permissions: (string | number)[];
}

interface AddRolesProperties {
  handleCancel: () => void;
  currentData?: RoleInterFace;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Role Add page function component
 */
function AddRole(properties: AddRolesProperties) {
  const dispatch = useAppDispatch();
  const { page, limit, permissionsList } = useAppSelector((state) => state.roles);
  const { handleCancel } = properties;
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const [roleDetails, setRoleDetails] = useState<RoleDetailInterface>({
    name: '',
    responsibility: null,
    permissions: [],
  });
  /**
   * Handle the input change function
   * @param name
   * @param value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setRoleDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (!stringValidation(1, 100, roleDetails.name) || roleDetails.permissions.length === 0) {
      return;
    }

    setFormStatus({
      showFormErrors: true,
      buttonLoader: true,
    });

    const request: {
      name: string;
      permissions: (string | number)[];
      responsibility?: string;
    } = {
      name: roleDetails.name,
      permissions: roleDetails.permissions,
    };

    if (roleDetails.responsibility) {
      request.responsibility = roleDetails.responsibility;
    }

    roleApi
      .createRoles(request)
      .then(() => {
        dispatch(fetchAllRoles({ page, limit }));
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          content: (
            <SuccessNotification
              title="Role created successfully"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCancel}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(fetchAllPermissions());
  }, []);

  return (
    <div className="add-role-form">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="form-body">
        <div className="form-field">
          <CustomInput
            name="name"
            inputLabel="Role Name"
            type="text"
            placeholder="Enter the name"
            valueRequired
            value={roleDetails.name}
            onChangeValue={handleInputChange}
            error={formStatus.showFormErrors && !stringValidation(1, 100, roleDetails.name)}
            errorMessage="Role name is required"
          />
        </div>
        <div className="form-field">
          <CustomInput
            name="responsibility"
            inputLabel="Responsibility"
            type="text"
            placeholder="Enter the Details"
            valueRequired
            value={roleDetails.responsibility}
            onChangeValue={handleInputChange}
            error={formStatus.showFormErrors}
            errorMessage='Responsibility is required'
          />
        </div>
        <div className="form-field">
          <CheckBoxButtons
            name="permissions"
            list={permissionsList}
            inputLabel="Permissions"
            valueRequired
            value={roleDetails.permissions}
            onChangeValue={handleInputChange}
            error={formStatus.showFormErrors && roleDetails.permissions.length === 0}
            errorMessage="Permission is required"
          />
        </div>
        <div className="role-form-footer">
          <div className="back-button-container">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleCancel} />
          </div>
          <div className="submit-button-container">
            <MyButton
              label="Submit"
              buttonType="submit"
              onClickFunc={submit}
              disableButton={formStatus.buttonLoader}
              loading={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddRole;
